import { useEffect, useRef, useState } from "react";
import { Tabs } from "../../components/common/Tabs"
import moment from "moment-timezone";
import DateRangePicker from "../../components/daterangepicker/DateRangePicker";
import { dateToPosition } from "../carrental/calendar/utlis";
import { DailyManifest } from "./DailyManifest";
import useAccessControl from "../../components/hooks/useAccessControl";
import Cookies from "js-cookie";
import { useTheme } from "../../components/hooks/useTheme";
import Availability from "../dashboard/Availability";
import Fleet from "../dashboard/Fleet";
import Extras from "../dashboard/Extras";
import Attemps from "../dashboard/Attemps";
import { useNavigate } from "react-router-dom";
import { Bookings } from "./Bookings";
import colorConfigs from "../../configs/colorConfigs";
import CustomFormatDatePicker from "../../components/common/CustomFormatDatePicker";
import useGeneral from "../../components/hooks/useGeneral";
import { getBranchesWithTimeZoneValue } from "../../services/branch";
import useTenant from "../../components/hooks/useTenant";
import Dropdown from "../../components/Dropdown";

const tabs = [
  {
    label: "Daily Manifest",
    accessName: "dashboarddailymanifest"
  },
  {
    label: "Bookings",
    accessName: "dashboardbookings"
  },
  {
    label: "Availability",
    accessName: "dashboardavailability"
  },
  {
    label: "Fleet",
    accessName: "dashboardfleet"
  },
  {
    label: "Extras",
    accessName: "dashboardextras"
  },
  {
    label: "Attempts",
    accessName: "dashboardattempts"
  }
]


export const Dashboard = () => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const { dateFormat, timeZoneName }: any = useGeneral();
  const { handleTenantError }: any = useTenant();
  const role = Cookies.get("role-jj-rental-ui") || '';
  const isAdmin = role?.toLowerCase() === 'admin';
  const navigate = useNavigate();
  const { permissions }: any = useAccessControl();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const dateRangePickeref = useRef<HTMLDivElement | null>(null);
  const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>(moment().startOf("month").subtract(1, 'month').format("MMM D, YYYY"));
  const [customEndDate, setCustomEndDate] = useState<string>(moment().format("MMM D, YYYY"));
  const [currentDateFormat, setCurrentDateFormat] = useState(
    customStartDate + " - " +
    customEndDate
  );
  const [date, setDate] = useState<string>(moment.tz(timeZoneName).format("YYYY-MM-DD"));
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [dateType, setDateType] = useState<string>("Custom Range");
  const [branchList, setBranchList] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any | null>(null);
  const [branchTimeZone, setBranchTimeZone] = useState<string>("");

    const tabsWithIndex = tabs.map((tab, index) => ({
    ...tab,
    originalIndex: index
  }));
  const allowedTab = tabsWithIndex?.filter((tab: any) => permissions[tab?.accessName] === true || isAdmin);
  const activeAllowedIndex = allowedTab.findIndex(tab => tab?.originalIndex === activeIndex)

  useEffect(() => {
    getBranchesWithTimeZoneValue(tenantId)
      .then((res) => {
        setBranchList([
          {
            value: 0,
            label: "All"
          },
          ...res?.map((item: any) => ({
            ...item,
            value: item?.id,
            label: item?.branchName
          }))
        ]);
        setSelectedBranch({ value: 0, label: "All" });
        handleTenantError(null);
      })
      .catch((error) => {
        handleTenantError(error);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleDropDown = (e: MouseEvent) => {
      if (!dateRangePickeref.current?.contains(e.target as Node)) setShowDateRangePicker(false);
    }

    document.addEventListener("click", handleDropDown);
    return () => document.removeEventListener("click", handleDropDown);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!timeZoneName) return;
    if (isMounted) return; // prevents re-running

    const tz = timeZoneName || "UTC";
    const today = moment.tz(tz).format("YYYY-MM-DD");

    setDate(today);
    setIsMounted(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZoneName, branchTimeZone])

  useEffect(() => {
    if (!date || activeIndex !== 0) return;

    let tz = timeZoneName || "UTC";

    if (selectedBranch?.value > 0 && selectedBranch?.timezoneName) {
      const match = selectedBranch?.timezoneName.match(/\((.*?)\)\s(.*?)\s/);
      if (match?.[2]) tz = match[2];
      setBranchTimeZone(tz);
    } else {
      setBranchTimeZone("");
    }

    const normalized = moment?.tz(date, "YYYY-MM-DD", tz)?.format("YYYY-MM-DD");

    if (normalized !== date) {
      setDate(normalized);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, activeIndex])

  const incrementDate = (days: number) => {
    setDate(prev => moment.tz(prev, "YYYY-MM-DD", branchTimeZone ? branchTimeZone : timeZoneName).add(days, "days").format("YYYY-MM-DD"));
  }

  const hanbleTabChange = (index: number) => {
    const selectedTab = allowedTab[index];
    setActiveIndex(selectedTab?.originalIndex);
  }

  return (
    <div className={`px-[24px] py-[20px] min-h-screen h-full ${themeStyles.main.tailwindSectionBg}`}>
      <div className={`w-full ${themeStyles.main.tailwindMainBg} border ${themeStyles.main.borderColor} rounded-lg p-[10px] pb-[40px] flex flex-col gap-2.51`}>

        <div className="flex items-center justify-between">

          <Tabs tabs={allowedTab} onChange={(index) => hanbleTabChange(index)} activeIndex={activeAllowedIndex} />

          <div className="flex items-center gap-2.5">

            <button className={`px-[8px] py-[6px] ${colorConfigs.btn.main.tailwind} rounded-lg border ${themeStyles.main.borderColor} text-white text-nowrap flex items-center gap-1.5`}
              onClick={() => {
                navigate("/carrental/reservations/addreservation");
              }}
            >
              <span className="material-symbols-outlined text-[25px] font-light">
                add
              </span>
              <span className="text-xs font-medium">New Reservation</span>
            </button>

            <Dropdown
              list={branchList}
              selectedItem={selectedBranch}
              setSelectedItem={setSelectedBranch}
            />

            {
              activeIndex === 0 ? (
                <>
                  <button
                    type="button"
                    className={`px-[8px] py-[6px] rounded-lg border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor}`}
                    onClick={() =>
                      setDate(() => {
                        const tz = (branchTimeZone ? branchTimeZone : timeZoneName) || "UTC"; // fallback
                        const now = moment.tz(tz).startOf("day");
                        const current = moment.tz(date, "YYYY-MM-DD", tz).startOf("day");

                        const nextDate = current.isSame(now, "day")
                          ? now.clone().add(1, "day") // Tomorrow
                          : now.clone();              // Today

                        return nextDate.format("YYYY-MM-DD");
                      })
                    }
                  >
                    <span className="text-xs font-medium text-nowrap">
                      {moment.tz(date, (branchTimeZone ? branchTimeZone : timeZoneName) || "UTC").isSame(
                        moment().tz((branchTimeZone ? branchTimeZone : timeZoneName) || "UTC"),
                        "day"
                      )
                        ? "Tomorrow"
                        : "Today"}
                    </span>
                  </button>

                  <button className={`px-[4px] py-[6px] rounded-lg border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} flex items-center justify-center`}>
                    <span
                      className="material-symbols-outlined text-[25px] font-light"
                      onClick={() => incrementDate(-1)}
                    >
                      chevron_left
                    </span>
                  </button>

                  <CustomFormatDatePicker
                    value={date}
                    onChange={setDate}
                    dateFormat={dateFormat}
                    required={true}
                    today={moment.tz(selectedBranch?.value > 0 ? branchTimeZone : timeZoneName).startOf("day")}
                    className={`px-[5px] py-[10px] ${dateFormat === "DD-MMM-YYYY" ? "w-[96px]" : "w-[86px]"} rounded-lg border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} text-xs text-nowrap font-semibold outline-none bg-transparent`}
                    showValidMessage={false}
                    leftShift={true}
                  />

                  <button className={`px-[4px] py-[6px] rounded-lg border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} flex items-center justify-center`}>
                    <span
                      className="material-symbols-outlined text-[25px] font-light"
                      onClick={() => incrementDate(1)}
                    >
                      chevron_right
                    </span>
                  </button>
                </>
              )
                :
                (
                  <div ref={dateRangePickeref}>
                    <div
                      className={`px-[4px] py-[6px] rounded-lg relative border ${themeStyles.main.borderColor} ${themeStyles.main.textSubHeadingColor} flex items-center gap-1.5 cursor-pointer`}
                      onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                    >
                      <span className="text-xs text-nowrap font-medium">
                        {currentDateFormat}
                      </span>
                      <span className="material-symbols-outlined text-[25px] font-extralight">
                        date_range
                      </span>

                    </div>
                    <DateRangePicker
                      zIndex={156}
                      setDateType={setDateType}
                      intialStartDate={customStartDate}
                      intialEndDate={customEndDate}
                      showDateRangePicker={showDateRangePicker}
                      setShowDateRangePicker={setShowDateRangePicker}
                      setDateRange={(startDate, endDate) => {
                        setCustomStartDate(startDate);
                        setCustomEndDate(endDate);
                        const differentInDays = dateToPosition(moment(startDate), moment(endDate));
                        if (differentInDays <= 0) {
                          setCurrentDateFormat(
                            moment(startDate).format('MMM D, YYYY')
                          );
                        } else {
                          setCurrentDateFormat(
                            moment(startDate).format('MMM D, YYYY') +
                            moment(endDate).format(' - MMM D, YYYY')
                          );
                        }
                      }}
                    />
                  </div>
                )
            }
          </div>
        </div>
        <div>
          {
            activeIndex === 0 && (
              <DailyManifest
                date={date}
                branchId={selectedBranch?.value}
                branchTimeZone={branchTimeZone}
              />
            )
          }

          {
            activeIndex === 1 && (
              <Bookings
                branchId={selectedBranch?.value || 0}
                startDate={customStartDate}
                endDate={customEndDate}
                dateType={dateType}
              />
            )
          }

          {
            activeIndex === 2 && (
              <Availability />
            )
          }

          {
            activeIndex === 3 && (
              <Fleet />
            )
          }

          {
            activeIndex === 4 && (
              <Extras />
            )
          }

          {
            activeIndex === 5 && (
              <Attemps />
            )
          }
        </div>
      </div>
    </div>
  )
}